import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Clock as ClockIcon, X } from "lucide-react";
import toast from "react-hot-toast";
import { attendanceService, authService } from "../../services/api";
import {
  evaluateCheckInPunch,
  loadAttendanceShifts,
} from "../../lib/attendancePolicyEngine";
import {
  getShiftOverrideForDay,
  setShiftOverrideForDay,
} from "../../lib/attendanceShiftOverride";
import {
  ATTENDANCE_PUNCH_UPDATED_EVENT,
  LEGACY_CHECKIN_KEY,
  LEGACY_LAST_SESSION_KEY,
  LEGACY_WORKLOG_KEY,
  dispatchPunchUpdated,
  employeeCheckInKey,
  employeeLastSessionKey,
  getLocalDayKey,
  readEmployeeWorklog,
  saveEmployeeWorklogDay,
} from "../../lib/attendancePunchStorage";

export const ADMIN_HOME_CHECKIN_KEY = LEGACY_CHECKIN_KEY;

function readStoredCheckIn(employeeId) {
  const key = employeeId ? employeeCheckInKey(employeeId) : LEGACY_CHECKIN_KEY;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { checkedIn: false, at: null };
    const t = Number(raw);
    if (Number.isFinite(t) && t > 0) return { checkedIn: true, at: t };
  } catch {
    /* ignore */
  }
  return { checkedIn: false, at: null };
}

function readLastSession(employeeId) {
  const key = employeeId ? employeeLastSessionKey(employeeId) : LEGACY_LAST_SESSION_KEY;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const inMs = Number(parsed.inMs);
    const outMs = Number(parsed.outMs);
    if (!Number.isFinite(inMs) || !Number.isFinite(outMs) || inMs <= 0 || outMs <= 0) {
      return null;
    }
    return { inMs, outMs };
  } catch {
    return null;
  }
}

function isSameLocalDay(aMs, bMs) {
  const a = new Date(aMs);
  const b = new Date(bMs);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatCheckInClock(ms) {
  if (ms == null || !Number.isFinite(ms)) return "—";
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatElapsedHMS(startMs, endMs) {
  if (startMs == null || endMs == null || !Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return "—";
  }
  const diff = Math.max(0, endMs - startMs);
  const totalSec = Math.floor(diff / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function persistCheckInFlag(employeeId, checkedIn, checkInAt) {
  const key = employeeId ? employeeCheckInKey(employeeId) : LEGACY_CHECKIN_KEY;
  try {
    if (checkedIn && checkInAt != null) {
      localStorage.setItem(key, String(checkInAt));
      if (employeeId) localStorage.setItem(LEGACY_CHECKIN_KEY, String(checkInAt));
    } else {
      localStorage.removeItem(key);
      if (!employeeId || employeeId === localStorage.getItem("hrms_current_employee_id")) {
        localStorage.removeItem(LEGACY_CHECKIN_KEY);
      }
    }
  } catch {
    /* ignore */
  }
}

function persistLastSession(employeeId, session) {
  const key = employeeId ? employeeLastSessionKey(employeeId) : LEGACY_LAST_SESSION_KEY;
  try {
    localStorage.setItem(key, JSON.stringify(session));
    if (employeeId) localStorage.setItem(LEGACY_LAST_SESSION_KEY, JSON.stringify(session));
  } catch {
    /* ignore */
  }
}

/**
 * Navbar check-in / check-out for the logged-in user's employee record.
 * Persists punches locally and via attendance API; notifies calendar to recalc PR/LOP.
 */
export default function AdminCheckInOut({ darkMode, variant = "navbar" }) {
  const [employeeId, setEmployeeId] = useState(null);
  const [apiReady, setApiReady] = useState(false);
  const [punchBusy, setPunchBusy] = useState(false);
  const [shiftOptions, setShiftOptions] = useState([]);
  const [todayShiftOverrideId, setTodayShiftOverrideId] = useState("");

  useEffect(() => {
    setShiftOptions(loadAttendanceShifts().filter((s) => s.status !== "inactive"));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authService.getCurrentUser();
        const id = res?.data?.employeeId ?? res?.data?.employee?.id ?? null;
        if (!cancelled && id != null) {
          const sid = String(id);
          setEmployeeId(sid);
          localStorage.setItem("hrms_current_employee_id", sid);
          try {
            const raw = localStorage.getItem("currentUser");
            if (raw) {
              const u = JSON.parse(raw);
              if (u && typeof u === "object" && !u.employeeId) {
                localStorage.setItem(
                  "currentUser",
                  JSON.stringify({ ...u, employeeId: id })
                );
              }
            }
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* offline / no employee link */
      } finally {
        if (!cancelled) setApiReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const initialCheck = employeeId ? readStoredCheckIn(employeeId) : readStoredCheckIn(null);
  const initialToday = employeeId ? readEmployeeWorklog(employeeId) : null;

  const [checkedIn, setCheckedIn] = useState(initialCheck.checkedIn);
  const [checkInAt, setCheckInAt] = useState(initialCheck.at ?? initialToday?.inMs ?? null);
  const [checkOutAt, setCheckOutAt] = useState(initialToday?.outMs ?? null);

  useEffect(() => {
    if (!employeeId) return;
    const ov = getShiftOverrideForDay(employeeId);
    setTodayShiftOverrideId(ov?.shiftId || "");
  }, [employeeId, checkedIn, checkInAt]);
  const [lastSession, setLastSession] = useState(() =>
    employeeId ? readLastSession(employeeId) : readLastSession(null)
  );
  const [, setTick] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);

  const isCompact = variant === "navbar";

  useEffect(() => {
    if (!apiReady || !employeeId) return;
    const stored = readStoredCheckIn(employeeId);
    const today = readEmployeeWorklog(employeeId);
    setCheckedIn(stored.checkedIn);
    setCheckInAt(stored.at ?? today?.inMs ?? null);
    setCheckOutAt(today?.outMs ?? null);
    setLastSession(readLastSession(employeeId));
  }, [apiReady, employeeId]);

  const updateDropdownPosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    persistCheckInFlag(employeeId, checkedIn, checkInAt);
  }, [checkedIn, checkInAt, employeeId]);

  useEffect(() => {
    if (!checkedIn || checkInAt == null) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [checkedIn, checkInAt]);

  useEffect(() => {
    if (!detailsOpen) return;
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [detailsOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!detailsOpen) return;
    const onMouseDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target.closest("[data-portal-checkin-details]")) return;
      setDetailsOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [detailsOpen]);

  const handleCheckIn = async () => {
    const t = Date.now();
    if (employeeId) {
      const gate = evaluateCheckInPunch({
        employee: { id: employeeId, employeeId },
        checkInMs: t,
      });
      (gate.warnings || []).forEach((msg) => toast(msg, { duration: 5000, icon: "⚠️" }));
    }
    setCheckInAt(t);
    setCheckOutAt(null);
    setCheckedIn(true);
    if (employeeId) saveEmployeeWorklogDay(employeeId, t, null);
    else dispatchPunchUpdated("legacy", getLocalDayKey(t));

    try {
      await attendanceService.checkIn();
    } catch (err) {
      console.warn("Check-in API failed; punch saved locally.", err);
    }
  };

  const handleCheckOut = async () => {
    const outMs = Date.now();
    const inMs = checkInAt;
    if (inMs != null) {
      const session = { inMs, outMs };
      setLastSession(session);
      setCheckOutAt(outMs);
      if (employeeId) {
        saveEmployeeWorklogDay(employeeId, inMs, outMs);
        persistLastSession(employeeId, session);
      } else {
        dispatchPunchUpdated("legacy", getLocalDayKey(inMs));
      }
    }
    setCheckedIn(false);

    try {
      await attendanceService.checkOut();
    } catch (err) {
      console.warn("Check-out API failed; punch saved locally.", err);
    }
  };

  const inMsForView = checkInAt;
  const outMsForView = checkedIn ? null : checkOutAt;

  const checkInClock = inMsForView != null ? formatCheckInClock(inMsForView) : null;
  const checkOutClock = outMsForView != null ? formatCheckInClock(outMsForView) : null;

  const workHours =
    inMsForView != null
      ? formatElapsedHMS(inMsForView, checkedIn ? Date.now() : outMsForView ?? Date.now())
      : null;

  const showLastSession =
    !checkedIn &&
    lastSession?.inMs != null &&
    lastSession?.outMs != null &&
    isSameLocalDay(lastSession.inMs, Date.now());
  const lastInClock = showLastSession ? formatCheckInClock(lastSession.inMs) : null;
  const lastOutClock = showLastSession ? formatCheckInClock(lastSession.outMs) : null;
  const lastTotal = showLastSession ? formatElapsedHMS(lastSession.inMs, lastSession.outMs) : null;

  const hasDetails = inMsForView != null || showLastSession;
  const displayInClock = inMsForView != null ? checkInClock : lastInClock;
  const displayOutClock = inMsForView != null ? checkOutClock ?? "—" : lastOutClock;
  const displayWorkHours = inMsForView != null ? workHours : lastTotal;

  const openDetails = () => {
    if (!hasDetails) return;
    updateDropdownPosition();
    setDetailsOpen(true);
  };

  const onTogglePunch = async () => {
    if (punchBusy) return;
    setPunchBusy(true);
    try {
      if (!checkedIn) {
        await handleCheckIn();
        setLastSession((prev) => {
          if (prev?.inMs && isSameLocalDay(prev.inMs, Date.now())) return prev;
          return null;
        });
      } else {
        await handleCheckOut();
      }
      if (hasDetails || checkInAt != null) {
        updateDropdownPosition();
        setDetailsOpen(true);
      }
    } finally {
      setPunchBusy(false);
    }
  };

  const detailsPanel =
    detailsOpen &&
    hasDetails &&
    createPortal(
      <div
        data-portal-checkin-details
        className={`fixed z-[10070] w-[min(92vw,16rem)] rounded-md border shadow-xl opacity-100 ${
          darkMode
            ? "border-slate-600 bg-slate-900 text-gray-100"
            : "border-slate-200 bg-white text-gray-900"
        }`}
        style={{
          top: `${dropdownPosition.top}px`,
          right: `${dropdownPosition.right}px`,
        }}
      >
        <div className="flex items-center justify-between gap-2 border-b border-slate-200/80 px-2.5 py-2 dark:border-slate-700/80">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
            Today
          </span>
          <button
            type="button"
            onClick={() => setDetailsOpen(false)}
            className={`rounded p-0.5 transition ${
              darkMode
                ? "text-slate-400 hover:bg-white/10 hover:text-slate-200"
                : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            }`}
            aria-label="Close check-in details"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="px-2.5 py-2">
          <div className="flex flex-wrap gap-1.5">
            <span className="inline-flex items-center rounded-full border border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-950/30 px-2 py-0.5 text-[10px] font-semibold text-emerald-900 dark:text-emerald-200">
              In&nbsp;{displayInClock}
            </span>
            <span className="inline-flex items-center rounded-full border border-rose-200 dark:border-rose-800 bg-rose-50/70 dark:bg-rose-950/30 px-2 py-0.5 text-[10px] font-semibold text-rose-900 dark:text-rose-200">
              Out&nbsp;{displayOutClock ?? "—"}
            </span>
          </div>
          {displayWorkHours && (
            <p className="mt-1.5 text-[10px] tabular-nums text-gray-500 dark:text-gray-400">
              Work Hours&nbsp;{displayWorkHours}
            </p>
          )}
          {employeeId && shiftOptions.length > 0 && (
            <div className="mt-2 border-t border-slate-200/80 pt-2 dark:border-slate-700/80">
              <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                Today&apos;s shift
              </label>
              <select
                className={`mt-1 w-full rounded border px-1.5 py-1 text-[11px] ${
                  darkMode
                    ? "border-slate-600 bg-slate-800 text-slate-100"
                    : "border-slate-300 bg-white text-slate-900"
                }`}
                value={todayShiftOverrideId}
                onChange={(e) => {
                  const id = e.target.value;
                  setTodayShiftOverrideId(id);
                  const sh = shiftOptions.find((s) => String(s.id) === id);
                  if (sh) {
                    setShiftOverrideForDay(employeeId, sh.id, sh.name);
                    toast.success(`Shift set to ${sh.name} for today`);
                  }
                }}
              >
                <option value="">Policy default</option>
                {shiftOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.shiftType || "fixed"})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[9px] text-slate-500 dark:text-slate-400">
                Change shift before check-in if you are on a different roster.
              </p>
            </div>
          )}
        </div>
      </div>,
      document.body
    );

  return (
    <div
      className={`flex flex-col items-end gap-0.5 font-sans antialiased ${
        darkMode ? "text-gray-300" : "text-gray-800"
      }`}
    >
      <div className="relative flex items-center gap-1.5">
        <button
          ref={btnRef}
          type="button"
          onMouseEnter={openDetails}
          onClick={onTogglePunch}
          disabled={punchBusy}
          className={`inline-flex items-center gap-1.5 rounded-full border font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#121212] ${
            isCompact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm shadow-sm"
          } ${
            checkedIn
              ? darkMode
                ? "border-rose-500/70 bg-rose-950/40 text-rose-200 hover:bg-rose-950/60"
                : "border-rose-500 bg-rose-50 text-rose-800 hover:bg-rose-100"
              : darkMode
              ? "border-emerald-500/70 bg-emerald-950/40 text-emerald-200 hover:bg-emerald-950/60"
              : "border-emerald-600 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
          } ${punchBusy ? "opacity-60 cursor-wait" : ""}`}
          aria-label={checkedIn ? "Check out" : "Check in"}
          aria-expanded={detailsOpen}
        >
          <ClockIcon
            className={isCompact ? "h-3.5 w-3.5 shrink-0" : "h-4 w-4 shrink-0"}
            aria-hidden
          />
          {checkedIn ? "Check Out" : "Check In"}
        </button>
        {detailsPanel}
      </div>
      {workHours && (
        <span
          className={`inline-flex items-center rounded-full border border-gray-200/90 dark:border-[#3C3C4C] bg-white/50 dark:bg-[#2a2a2a]/80 px-2 py-0.5 tabular-nums font-semibold text-gray-800 dark:text-gray-100 ${
            isCompact ? "text-[10px]" : "text-xs"
          }`}
        >
          Work Hours&nbsp;{workHours}
        </span>
      )}
    </div>
  );
}

export { ATTENDANCE_PUNCH_UPDATED_EVENT };
